import { useEffect, useRef } from "react";
import { clampFrac } from "./constants";

// Keyboard nudge, pointer drag (1:1, grouped) and pinch-to-resize for canvas layers
export function useLayerInteractions({ layers, setLayers, selectedIds, setSelectedIds, setLastSelectedId, setSelected, multiMode, canvasRef }) {
  // --- Keyboard nudge: arrow keys move all selected layers; Shift = bigger steps ---
  useEffect(() => {
    const onKey = (e) => {
      if (selectedIds.size === 0) return;
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || target?.isContentEditable) return;
      const keys = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (!keys[e.key]) return;
      e.preventDefault();
      const step = e.shiftKey ? 0.04 : 0.01;
      const [dx, dy] = keys[e.key];
      setLayers((prev) => prev.map((l) => selectedIds.has(l.id) ? {
        ...l,
        x: clampFrac((l.x ?? 0.5) + dx * step),
        y: clampFrac((l.y ?? 0.5) + dy * step),
      } : l));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIds, setLayers]);

  // --- Pointer drag: layers follow the cursor/finger 1:1; grouped layers move together ---
  const dragRef = useRef(null);
  const startDrag = (e, id) => {
    if (pinchRef.current.active || !e.isPrimary) return;
    e.stopPropagation();
    let ids, tapToggle = false;
    if ((multiMode || e.shiftKey) && !selectedIds.has(id)) {
      ids = new Set(selectedIds); ids.add(id);
      setSelectedIds(ids); setLastSelectedId(id);
    } else if (selectedIds.has(id)) {
      // Already selected: drag the group; a tap without movement deselects it in multi mode
      tapToggle = selectedIds.size > 1 ? ((multiMode || e.shiftKey) ? "remove" : "collapse") : false;
      ids = selectedIds; setLastSelectedId(id);
    } else {
      setSelected(id); ids = new Set([id]);
    }
    const starts = new Map(layers.filter((l) => ids.has(l.id)).map((l) => [l.id, { x: l.x ?? 0.5, y: l.y ?? 0.5 }]));
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { pointerId: e.pointerId, id, tapToggle, moved: false, sx: e.clientX, sy: e.clientY, w: rect.width, h: rect.height, starts };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const moveDrag = (e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (pinchRef.current.active) { dragRef.current = null; return; }
    if (Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > 4) d.moved = true;
    const dx = (e.clientX - d.sx) / d.w, dy = (e.clientY - d.sy) / d.h;
    setLayers((prev) => prev.map((l) => {
      const st = d.starts.get(l.id);
      return st ? { ...l, x: clampFrac(st.x + dx), y: clampFrac(st.y + dy) } : l;
    }));
  };
  const endDrag = (e) => {
    const d = dragRef.current;
    if (d?.pointerId !== e.pointerId) return;
    dragRef.current = null;
    if (d.tapToggle === "collapse" && !d.moved) setSelected(d.id);
    else if (d.tapToggle && !d.moved) {
      const next = new Set(selectedIds); next.delete(d.id);
      setSelectedIds(next); setLastSelectedId([...next].pop() ?? null);
    }
  };

  // --- Pinch-to-resize on mobile for all selected layers ---
  const pinchRef = useRef({ active: false, startDist: 0, starts: new Map() });
  const pinchDist = (touches) => {
    const [a, b] = [touches[0], touches[1]];
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const onCanvasTouchStart = (e) => {
    if (e.touches.length !== 2 || selectedIds.size === 0) return;
    const starts = new Map();
    layers.forEach((l) => {
      if (selectedIds.has(l.id)) starts.set(l.id, l.type === "text" ? l.size : l.width);
    });
    pinchRef.current = { active: true, startDist: pinchDist(e.touches), starts };
    e.preventDefault();
  };
  const onCanvasTouchMove = (e) => {
    if (!pinchRef.current.active || e.touches.length !== 2) return;
    const factor = pinchDist(e.touches) / Math.max(1, pinchRef.current.startDist);
    e.preventDefault();
    setLayers((prev) => prev.map((l) => {
      const start = pinchRef.current.starts.get(l.id);
      if (!start) return l;
      if (l.type === "text") {
        return { ...l, size: Math.max(12, Math.min(140, Math.round(start * factor))) };
      }
      const w = Math.max(40, Math.min(500, Math.round(start * factor)));
      const ratio = l.height / Math.max(1, l.width);
      return { ...l, width: w, height: Math.max(40, Math.min(500, Math.round(w * ratio))) };
    }));
  };
  const onCanvasTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current.active = false;
  };

  return { startDrag, moveDrag, endDrag, onCanvasTouchStart, onCanvasTouchMove, onCanvasTouchEnd };
}
